import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import apiService from "../services/api";
import { useAuth } from "./AuthContext";
import toast from "react-hot-toast";

const CartContext = createContext();

export function useCart() {
  return useContext(CartContext);
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const toggleCart = () => setIsCartOpen((prev) => !prev);

  const fetchCart = useCallback(async () => {
    if (!user) {
      setCart(null);
      return;
    }
    // Only fetch cart for customer users
    if (user.role !== "customer") {
      setCart(null);
      return;
    }
    try {
      const data = await apiService.getCart();
      setCart(data);
    } catch (error) {
      console.error("Failed to fetch cart:", error);
    }
  }, [user]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId, quantity = 1, skuId = null) => {
    setLoading(true);
    try {
      await apiService.addToCart(productId, quantity, skuId);
      await fetchCart();
      toast.success("Added to cart");
      return true;
    } catch (error) {
      console.error("Failed to add to cart:", error);
      toast.error("Failed to add to cart");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    setLoading(true);
    try {
      await apiService.updateCartItem(itemId, quantity);
      await fetchCart();
      return true;
    } catch (error) {
      console.error("Failed to update cart item:", error);
      toast.error("Failed to update cart item");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    setLoading(true);
    try {
      await apiService.removeFromCart(itemId);
      await fetchCart();
      toast.success("Removed from cart");
      return true;
    } catch (error) {
      console.error("Failed to remove from cart:", error);
      toast.error("Failed to remove from cart");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    setLoading(true);
    try {
      await apiService.clearCart();
      setCart(null);
      return true;
    } catch (error) {
      console.error("Failed to clear cart:", error);
      toast.error("Failed to clear cart");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const cartCount = cart?.items?.length || 0;

  const value = {
    cart,
    loading,
    fetchCart,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    cartCount,
    isCartOpen,
    toggleCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
